import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";

import { Box, Flex, Text, Divider, Spinner, Tooltip } from "@chakra-ui/react";

import { TextPrimary, TicketCard } from "theme";

import Icon from "@components/Icon";
import TicketInput from "@components/TicketInput";
import TicketInputRightSection from "@components/TicketInputRightSection";
import UndelegationMessage from "@components/UndelegationMessage";
import UnstakingLimit from "@components/UnstakingLimit";

import {
  isFloat,
  isNumber,
  toFixedWithoutRounding,
  toStringPrecision,
} from "@utils/common";
import { formatNumber } from "@utils/globalCommon";

import { TicketWalletConnect, useDappSDKProvider } from "dapp-sdk-v2";

import { Binance, Eth, Polygon, Hedera, Fantom, SD } from "icons";

import usePageData from "@hooks/usePageData";

import { PageContext } from "@providers/PageProvider";
import CMSJson from "@constants/cms.json";
import {
  BNB,
  ETH,
  HEDERA,
  POLYGON,
  FANTOM,
  showTransactionCost,
  showTransactionFee,
  getTransitionFee,
  SD as SD_TOKEN,
} from "@constants/constants";

import { GasFeesItem } from "@customtypes/global";
import { IIndexable } from "@customtypes/common";
import BaseImage from "dapp-sdk-v2/components/Image";
import UnboundPeriod from "@components/UnboundPeriod";
import { AiOutlineInfoCircle } from "react-icons/ai";

const TOKEN = process.env.NEXT_PUBLIC_TOKEN;

const DEFAULT_UNSTAKE_TEXT = TOKEN === SD_TOKEN ? "withdraw" : "unstake";

export const toFixed = (value: number | string, fact = 4) => {
  const rx = new RegExp(`^\\d+(?:\\.\\d{0,${fact}})?`);
  return value.toString().match(rx)?.[0];
};

const DEFAULT_UNSTAKE_LIMITS: IIndexable = {
  min: null,
  max: null,
};

interface UnStakeProps {
  gasFees: undefined | GasFeesItem;
}
const Unstake = ({ gasFees }: UnStakeProps) => {
  const {
    isConnected,
    walletAddress: address,
    tokenXAmount,
    tokenXSymbol,
    conversionData,
    tvlData,
    unstakeAmount,
    penalty,
    isLoadingPenalty,
    limits,
    tokenAmount,
    setStakeData,
    boostedRewardsAmount,
  } = useDappSDKProvider();

  const [error, setError] = useState("");
  const [unstakeLimits, setUnstakeLimits] = useState(DEFAULT_UNSTAKE_LIMITS);

  const handleUnstakeAmountChange = useCallback((val: string) => {
    setStakeData({
      unstakeAmount: val,
    });
  }, []);

  const penaltyPerc = useMemo(() => {
    if (!unstakeAmount) return "0";
    if (penalty === undefined) return "0";
    if (penalty === 0 || !unstakeAmount) return "0";
    return toFixed((penalty / Number(unstakeAmount)) * 100);
  }, [penalty, unstakeAmount]);

  const conversions = useMemo(() => {
    const conversionToERC20 = conversionData?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
    };
  }, [conversionData?.exchangeRate]);

  const data = useContext(PageContext);

  const { isSafeMode } = data;

  useEffect(() => {
    if (limits) {
      setUnstakeLimits(limits.unstake);
    }
  }, [limits]);

  const {
    approve_unstaking_cta = "Approve Unstaking",
    switch_network_cta = "Switch Network",
    unstake_cta = "Unstake",
    wallet_connect_cta = "Connect wallet",
    erc20_token = TOKEN,
    x_token = process.env.NEXT_PUBLIC_XTOKEN,
    max_fraction_digits = 2,
    undelegation_warning_message,
    unstake_placeholder = "Enter Amount",
    input_max_cta,
    input_balance_cta,
    undelegation_warning_message_safe_mode,
    unstaking_limit,
    swap_cta,
    swap_link,
    unbounding_period,
  } = usePageData("$.page.data")[0] || {};

  const convertedAmount = useMemo(() => {
    if (unstakeAmount && isNumber(unstakeAmount)) {
      const conversionValue =
        Number(unstakeAmount) * Number(conversions.toERC20);
      const value = conversionValue - (penalty || 0);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              max_fraction_digits
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [unstakeAmount, conversions, max_fraction_digits, penalty]);

  const transactionCost = useMemo(() => {
    // if (gasFees?.unstake && tvlData?.tvl?.usdPrice) {
    //   return gasFees?.unstake * tvlData?.tvl?.usdPrice;
    // }
    if (gasFees?.usdUnstake) {
      return gasFees?.usdUnstake;
    }
  }, [gasFees, tvlData?.tvl?.usdPrice]);

  const transactionFee = useMemo(() => {
    if (tvlData?.tvl?.usdPrice) {
      return getTransitionFee(tvlData?.tvl?.usdPrice);
    }
  }, [tvlData]);

  const checkInput = (value = unstakeAmount.trim()) => {
    if (!isConnected) return "";
    let error = "";
    if (!+value || Number.isNaN(+value)) {
      error = "This field can't be blank or zero. Please provide valid input.";
    } else if (Number(value) > Number(tokenXAmount)) {
      error = `You can't ${DEFAULT_UNSTAKE_TEXT} more than available ${tokenXSymbol} ${
        TOKEN === SD_TOKEN ? "holding" : "balance"
      }`;
    } else if (transactionFee && Number(transactionFee) > Number(tokenAmount)) {
      error = `You don't have enough ${erc20_token}`;
    } else if (unstakeLimits.min && Number(value) < Number(unstakeLimits.min)) {
      error = `Please note that the minimum ${DEFAULT_UNSTAKE_TEXT} amount allowed in a single transaction is  ${Number(
        unstakeLimits.min
      )} ${erc20_token}`;
    } else if (
      TOKEN !== FANTOM &&
      TOKEN !== SD_TOKEN &&
      unstakeLimits.max &&
      Number(value) > Number(unstakeLimits.max)
    ) {
      error = `Please note that the maximum ${DEFAULT_UNSTAKE_TEXT} amount allowed in a single transaction is ${Number(
        unstakeLimits.max
      )} ${erc20_token}`;
    }
    return error;
  };

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        handleUnstakeAmountChange(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= max_fraction_digits) {
          handleUnstakeAmountChange(val === "." ? "0." : val);
        }
      }
    }
  };

  useEffect(() => {
    if (unstakeAmount !== "") {
      const error = checkInput(unstakeAmount);
      setError(error);
    }
  }, [unstakeAmount]);

  useEffect(() => {
    if (unstakeAmount) {
      const error = checkInput(unstakeAmount);
      setError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenXAmount, address]);

  const handleMaxClick = () => {
    handleUnstakeAmountChange(tokenXAmount.toString());
  };

  // const unStakeCallBack = useCallback(() => {
  //   handleUnstakeAmountChange("");
  // }, [handleUnstakeAmountChange]);

  const getTokenIcon = useCallback(() => {
    switch (TOKEN) {
      case ETH:
        return Eth;
      case BNB:
        return Binance;
      case POLYGON:
        return Polygon;
      case HEDERA:
        return Hedera;
      case FANTOM:
        return Fantom;
      case "SD":
        return SD;
      default:
        return "";
    }
  }, []);

  const getTooltip = useCallback(
    ({
      tooltipHeading,
      tooltipSubheading,
    }: {
      tooltipHeading?: string | ReactNode;
      tooltipSubheading?: string | ReactNode;
    }) => {
      return (
        <Tooltip
          placement="top"
          p="8px"
          borderRadius="8px"
          w="220px"
          label={
            <Flex flexDir="column" gap="8px" color="textSecondary">
              <Flex flexDir="column" gap="4px">
                {tooltipHeading && <Text>{tooltipHeading}</Text>}
                {tooltipSubheading && <Text>{tooltipSubheading}</Text>}
              </Flex>
            </Flex>
          }
          closeDelay={2000}
          bg="tooltipBackground"
        >
          <Text ml="4px" color="textSecondary" fontSize="14px">
            <AiOutlineInfoCircle />
          </Text>
        </Tooltip>
      );
    },
    []
  );

  return (
    <>
      <TicketCard>
        <Flex w="100%" alignItems="center">
          <Flex
            alignItems={"flex-end"}
            justifyContent="space-between"
            flexGrow={1}
            flexDir={{ base: "column", md: "row" }}
          >
            <Box display={{ base: "none", xl: "block" }} h="fit-content">
              <Text fontWeight="400">{unstake_placeholder}</Text>
            </Box>
            {TOKEN === "SD" && isConnected ? (
              <Flex flexDirection={"column"} alignItems={"flex-end"}>
                <Flex
                  whiteSpace={"pre-wrap"}
                  alignItems={"center"}
                  fontWeight={{ base: "700", md: "600" }}
                  fontSize={{ base: "12px", md: "14px" }}
                  marginBottom={2}
                >
                  <Text as="span">My holdings : </Text>
                  <Text as="span">
                    {formatNumber(Number(tokenXAmount), false, 2, true) || "-"}{" "}
                    {TOKEN}
                  </Text>
                  {getTooltip({
                    tooltipHeading:
                      "My Holdings = Your delegated SD + Fee earned for delegation",
                    tooltipSubheading: (
                      <>
                        You can withdraw your <strong>My Holdings</strong> from
                        the withdraw tab
                      </>
                    ),
                  })}
                </Flex>
                <Flex
                  whiteSpace={"pre-wrap"}
                  alignItems={"center"}
                  fontWeight={{ base: "700", md: "600" }}
                  fontSize={{ base: "12px", md: "14px" }}
                >
                  <Text as="span">Boosted Rewards : </Text>
                  <Text as="span">
                    {formatNumber(
                      Number(boostedRewardsAmount),
                      false,
                      2,
                      true
                    ) || "-"}{" "}
                    {TOKEN}
                  </Text>
                  {getTooltip({
                    tooltipHeading: (
                      <>
                        Boosted rewards are extra SD rewards for early joiners.
                        It will be automatically sent to your wallet with any{" "}
                        <strong>My Holdings</strong> withdrawal claim
                      </>
                    ),
                  })}
                </Flex>
              </Flex>
            ) : null}
          </Flex>
        </Flex>

        <TicketInput
          inputRightElement={
            <TicketInputRightSection
              onClick={handleMaxClick}
              token={{
                balance: tokenXAmount,
                symbol: tokenXSymbol,
              }}
              maxCTA={input_max_cta}
              inputBalanceCTA={input_balance_cta}
              isConnected
              isBalanceDisabled={TOKEN === SD_TOKEN}
            />
          }
          error={error}
          isDisabled={false}
          onChange={(value) => handleChange(value)}
          value={unstakeAmount}
          maxFractionDigits={max_fraction_digits}
        />

        {error && (
          <Flex alignItems="center" mt="1rem">
            <BaseImage
              src="/error.svg"
              width={20}
              height={20}
              alt="error-icon"
            />
            <Text ml="0.5rem" color="error" fontSize="12px" fontWeight="400">
              {error ? error : "error"}
            </Text>
          </Flex>
        )}

        <Flex
          justifyContent="space-between"
          flexDir="column"
          mt="2rem"
          fontWeight="400"
          fontSize={{ base: "12px", md: "14px" }}
        >
          {TOKEN !== "SD" ? (
            <>
              <Flex
                alignItems="center"
                justifyContent="space-between"
                fontWeight={{ base: "700", md: "600" }}
              >
                <Text mr="0.5rem">You will get</Text>

                <Flex>
                  <Icon Icon={getTokenIcon()} width="20px" height="20px" />

                  <TextPrimary ml="0.5rem">
                    {convertedAmount || 0} {erc20_token}
                  </TextPrimary>
                </Flex>
              </Flex>

              <Divider bg="seperator" mt="1rem" />

              {conversions.toERC20 ? (
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  mt="1rem"
                >
                  <Text mr="0.5rem">Exchange rate</Text>
                  <Text>
                    1 {x_token} ={" "}
                    {toFixedWithoutRounding(conversions.toERC20.toString(), 6)}{" "}
                    {erc20_token}
                  </Text>
                </Flex>
              ) : null}
            </>
          ) : null}

          {TOKEN === "FANTOM" && (
            <Flex
              alignItems="center"
              justifyContent="space-between"
              mt="8px"
              fontWeight={{ base: "700", md: "600" }}
            >
              <Text mr="0.5rem">Penalty</Text>

              <Flex>
                <TextPrimary ml="0.5rem">
                  {isLoadingPenalty ? <Spinner /> : `${penaltyPerc}%`}
                </TextPrimary>
              </Flex>
            </Flex>
          )}

          {showTransactionCost() && transactionCost ? (
            <Flex alignItems="center" justifyContent="space-between" mt="8px">
              <Text mr="0.5rem">Transaction cost</Text>
              <Text>{`$${formatNumber(transactionCost, false, 2, true)}`}</Text>
            </Flex>
          ) : null}

          {showTransactionFee() && transactionFee ? (
            <Flex alignItems="center" justifyContent="space-between" mt="8px">
              <Text mr="0.5rem">Transaction fees</Text>
              <Text>{`${transactionFee} ${erc20_token}`}</Text>
            </Flex>
          ) : null}

          {limits?.unstake?.max !== null &&
          limits?.unstake?.max !== undefined &&
          unstaking_limit?.enabled ? (
            <Flex
              bg="backgroundPrimary"
              mt="1rem"
              p="1rem"
              borderRadius="12px"
              flexDir="column"
              gap="12px"
            >
              <UnstakingLimit token={TOKEN === "SD" ? TOKEN : x_token} />
              {unbounding_period?.enabled ? <UnboundPeriod /> : null}
            </Flex>
          ) : null}

          {/* {unbounding_period?.enabled ? (
            <UnstakingLimit token={TOKEN === "SD" ? TOKEN : x_token} />
          ) : null} */}
        </Flex>

        <TicketWalletConnect
          type="unstake"
          error={error}
          walletConnectText={wallet_connect_cta}
          switchNetworkText={switch_network_cta}
          primaryActionText={unstake_cta}
          approveActionText={approve_unstaking_cta}
          // primaryActionCallBack={unStakeCallBack}
          setError={setError}
          cmsData={CMSJson}
          secondaryText={swap_cta}
          secondaryLink={swap_link}
          unstakeLimits={unstakeLimits}
        />

        {undelegation_warning_message ? (
          <Box mt="1rem">
            <UndelegationMessage
              message={
                isSafeMode
                  ? undelegation_warning_message_safe_mode
                  : undelegation_warning_message
              }
            />
          </Box>
        ) : null}
      </TicketCard>
    </>
  );
};

export default Unstake;
